import React, { useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import insytix from "../../../Assets/insytix_logo.png";
import { useAuthContext } from "../../../hooks";
import {
  getLoginEmail,
  InsytixPayslip,
  isPayslipReleased,
  latestReleasedMonth,
  loadLoginPayslip,
  payslipReleaseDate,
} from "../../../api/InsytixEmployees";
import PaySlipSc from "./index.sc";

const COMPANY = {
  addressLine1: "4/96 Balaji Nagar, Sengottai Road,",
  addressLine2: "Panpozhi, Tenkasi - 627809",
};

const inr = (amount: number) =>
  (Number(amount) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const show = (value: unknown) => {
  const text = String(value ?? "").trim();
  if (!text || text === "null" || text === "undefined") return "—";
  return text;
};

const ones = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];
const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

const twoDigits = (value: number) => {
  if (value < 20) return ones[value];
  return `${tens[Math.floor(value / 10)]}${value % 10 ? ` ${ones[value % 10]}` : ""}`;
};

const inWords = (value: number) => {
  const amount = Math.round(value);
  if (amount === 0) return "Zero Rupees Only";
  const crore = Math.floor(amount / 10000000);
  const lakh = Math.floor((amount % 10000000) / 100000);
  const thousand = Math.floor((amount % 100000) / 1000);
  const hundred = Math.floor((amount % 1000) / 100);
  const rest = amount % 100;
  const parts = [
    crore ? `${twoDigits(crore)} Crore` : "",
    lakh ? `${twoDigits(lakh)} Lakh` : "",
    thousand ? `${twoDigits(thousand)} Thousand` : "",
    hundred ? `${ones[hundred]} Hundred` : "",
    rest ? twoDigits(rest) : "",
  ].filter(Boolean);
  return `${parts.join(" ")} Rupees Only`;
};

const formatDays = (value: number) => {
  const rounded = Math.round((Number(value) || 0) * 100) / 100;
  return rounded.toFixed(2).replace(/\.?0+$/, "");
};

const monthName = (month: string) => {
  const [year, mon] = month.split("-").map(Number);
  if (!year || !mon) return month;
  return new Date(year, mon - 1, 1).toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const emptySlip = (month: string): InsytixPayslip => ({
  employee_id: null,
  name: null,
  email: null,
  designation: null,
  doj: null,
  acc_no: null,
  bank_name: null,
  basic_pay: 0,
  working_days: 0,
  paid_days: 0,
  lop_days: 0,
  lop_amount: 0,
  net_pay: 0,
  month,
  linked: false,
});

function Index() {
  const auth = useAuthContext();
  const slipRef = useRef<HTMLDivElement>(null);
  const releasedMonth = latestReleasedMonth();
  const [monthValue, setMonthValue] = useState(releasedMonth);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [slip, setSlip] = useState<InsytixPayslip>(() => emptySlip(releasedMonth));
  const [slipError, setSlipError] = useState("");

  const email = getLoginEmail(auth);
  const orgId = auth?.userInfo?.orgs?.[0]?.id || 100000000001;
  const orgUserId = auth?.userInfo?.orgs_user_id?.[0];

  const monthLabel = useMemo(() => monthName(monthValue), [monthValue]);

  useEffect(() => {
    if (!email || !monthValue) return;
    if (!isPayslipReleased(monthValue)) {
      const release = payslipReleaseDate(monthValue).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      setSlip(emptySlip(monthValue));
      setSlipError(`The ${monthName(monthValue)} payslip is available on ${release}.`);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSlipError("");
    loadLoginPayslip(orgId, email, monthValue, orgUserId)
      .then((data) => {
        if (cancelled) return;
        setSlip({ ...emptySlip(monthValue), ...data });
        if (!data.linked) {
          setSlipError("No Insytix employee record is linked to this login.");
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setSlip(emptySlip(monthValue));
          setSlipError(error?.message || "Could not load payslip details for this month.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email, monthValue, orgId, orgUserId]);

  const employeeName = slip.name || "Employee";
  const totalEarnings = Number(slip.basic_pay) || 0;
  const totalDeductions = Number(slip.lop_amount) || 0;
  const netPay = Number.isFinite(Number(slip.net_pay))
    ? Number(slip.net_pay)
    : totalEarnings - totalDeductions;
  const released = isPayslipReleased(monthValue);

  const handleDownloadPDF = async () => {
    if (!slipRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(slipRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
      });
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 8;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin, margin, imgWidth, imgHeight);
      pdf.save(`Insytix_Payslip_${monthValue}.pdf`);
    } catch (error) {
      console.error("Error generating PDF: ", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <PaySlipSc>
      <div className="payslip-toolbar">
        <label>
          Payslip month
          <input
            type="month"
            max={releasedMonth}
            value={monthValue}
            onChange={(event) => setMonthValue(event.target.value)}
          />
        </label>
        <button
          type="button"
          onClick={handleDownloadPDF}
          disabled={downloading || loading || !released || !slip.linked}
        >
          {downloading ? "Preparing PDF..." : "Download payslip"}
        </button>
      </div>

      <div className="slip" ref={slipRef}>
        <header className="slip-head">
          <div>
            <img src={insytix} alt="Insytix" />
            <div className="slip-addr">
              <div>{COMPANY.addressLine1}</div>
              <div>{COMPANY.addressLine2}</div>
            </div>
          </div>
          <div className="slip-meta">
            <div className="slip-title">Payslip</div>
            <div className="slip-month">For {monthLabel}</div>
          </div>
        </header>

        {loading ? <p className="slip-error">Loading payslip...</p> : null}
        {slipError ? <p className="slip-error">{slipError}</p> : null}
        {released ? (
        <>
        <table className="info-table">
          <colgroup>
            <col className="col-field" />
            <col className="col-val" />
            <col className="col-field" />
            <col className="col-val" />
          </colgroup>
          <tbody>
            <tr>
              <td className="field">Employee name</td>
              <td className="val">{show(employeeName)}</td>
              <td className="field">Employee ID</td>
              <td className="val">{show(slip.employee_id)}</td>
            </tr>
            <tr>
              <td className="field">Designation</td>
              <td className="val">{show(slip.designation)}</td>
              <td className="field">Date of joining</td>
              <td className="val">{show(slip.doj)}</td>
            </tr>
            <tr>
              <td className="field">Pay period</td>
              <td className="val">{monthLabel}</td>
              <td className="field">Work email</td>
              <td className="val">{show(slip.email || email)}</td>
            </tr>
            <tr>
              <td className="field">Account number</td>
              <td className="val">{show(slip.acc_no)}</td>
              <td className="field">Bank name</td>
              <td className="val">{show(slip.bank_name)}</td>
            </tr>
            <tr>
              <td className="field">Paid days</td>
              <td className="val">{formatDays(slip.paid_days)}</td>
              <td className="field">LOP days</td>
              <td className="val">{formatDays(slip.lop_days)}</td>
            </tr>
          </tbody>
        </table>

        <table className="pay-table">
          <colgroup>
            <col className="col-item" />
            <col className="col-amt" />
            <col className="col-item" />
            <col className="col-amt" />
          </colgroup>
          <tbody>
            <tr>
              <td className="head">Earnings</td>
              <td className="head amt">Amount (INR)</td>
              <td className="head">Deductions</td>
              <td className="head amt">Amount (INR)</td>
            </tr>
            <tr>
              <td>Basic salary</td>
              <td className="amt">{inr(totalEarnings)}</td>
              <td>Loss of pay</td>
              <td className="amt">{inr(totalDeductions)}</td>
            </tr>
            <tr>
              <td className="total">Gross earnings</td>
              <td className="amt total">{inr(totalEarnings)}</td>
              <td className="total">Total deductions</td>
              <td className="amt total">{inr(totalDeductions)}</td>
            </tr>
          </tbody>
        </table>

        <section className="payable">
          <div className="payable-row">
            <span>Net payable ( Gross earnings - Total deductions )</span>
            <strong>INR {inr(netPay)}</strong>
          </div>
          <div className="payable-words">{inWords(netPay)}</div>
        </section>

        <footer className="slip-note">
          <div>This is a computer-generated payslip and does not require a signature.</div>
        </footer>
        </>
        ) : null}
      </div>
    </PaySlipSc>
  );
}

export default Index;
